/**
 * CheckoutStepper component
 *
 * Adaptado de car-ecommerce-app.
 * Muestra progreso del checkout con 3 estados: active (actual), previous (completados), normal
 *
 * Patrón: thin presentational, todo el styling via theme centralizado
 */

import { FC, CSSProperties } from 'react';
import { translate } from '../../i18n';
import { stepperStyles } from './styles';
import type { CheckoutStepperProps, StepperStep } from './types';

/**
 * Helper para fusionar estilos
 */
const mergeStyles = (...styles: readonly (CSSProperties | undefined)[]): CSSProperties =>
  Object.assign({}, ...styles.filter(Boolean));

/**
 * CheckoutStepper - Componente raíz
 * Renderiza pasos con línea de progreso de fondo
 */
export const CheckoutStepper: FC<CheckoutStepperProps> = ({
  steps,
  activeStep,
  testID = 'checkout-stepper',
  accessibilityLabel = translate('paymentsUI.checkoutStepper.ariaLabel'),
}) => {
  const activeIndex = steps.findIndex((step) => step.step === activeStep);

  return (
    <div
      style={stepperStyles.stepperContainer}
      data-testid={testID}
      aria-label={accessibilityLabel}
      role="progressbar"
      aria-valuenow={activeIndex + 1}
      aria-valuemin={1}
      aria-valuemax={steps.length}
    >
      {/* Línea de fondo */}
      <div style={stepperStyles.stepperLine} aria-hidden="true" />

      {/* Pasos */}
      <div style={stepperStyles.stepper}>
        {steps.map((step, index) => {
          const isActive = activeStep === step.step;
          const isPrevious = activeIndex >= 0 && index < activeIndex;

          const stepContainerStyle: CSSProperties = mergeStyles(
            stepperStyles.stepContainer,
            index === 0
              ? stepperStyles.alignLeft
              : index === steps.length - 1
              ? stepperStyles.alignRight
              : undefined
          );

          const stepNumberStyle: CSSProperties = mergeStyles(
            stepperStyles.stepNumberContainer,
            isActive ? stepperStyles.stepNumberContainerActive : undefined,
            isPrevious ? stepperStyles.stepNumberContainerPrevious : undefined
          );

          const stepLabelStyle: CSSProperties = mergeStyles(
            stepperStyles.stepLabel,
            isActive ? stepperStyles.stepLabelActive : undefined,
            isPrevious ? stepperStyles.stepLabelPrevious : undefined
          );

          return (
            <div
              key={step.step}
              style={stepContainerStyle}
              data-testid={`${testID}-step-${index}`}
              role="listitem"
            >
              <div style={stepperStyles.step}>
                {/* Círculo numerado */}
                <div style={stepNumberStyle} aria-current={isActive ? 'step' : undefined}>
                  <div style={stepperStyles.stepNumber}>{step.number}</div>
                </div>

                {/* Etiqueta */}
                <div style={stepLabelStyle}>{step.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export type { CheckoutStepperProps, StepperStep };
